// ─────────────────────────────────────────────────────────────────────────────
// Assumir o ticket ao clicar em "Atender" — só a REGRA.
//
// Clicar em "Atender" numa fila do início marca o ticket como "Sup - Em
// Atendimento" no nome do operador. Quem faz o POST é `mysuiteApi`; aqui mora
// a única pergunta que precisa de resposta antes dele: **pode assumir?**
//
// A regra é conservadora de propósito. Marcar um ticket em nome de quem clicou
// tira ele da fila de outra pessoa — e no MySuite isso não avisa ninguém. Por
// isso o Orion só assume o que não tem dono de fato: ticket na fila genérica de
// Análise Suporte, ou que já é do próprio operador.
//
// Este módulo não conhece React nem rede, e por isso roda inteiro sob `node`
// em scripts/teste-assumir-ticket.mjs.
// ─────────────────────────────────────────────────────────────────────────────

// Os mesmos números de mysuiteApi.js, repetidos aqui de propósito: importar de
// lá traria junto o Supabase e a configuração de ambiente, e este arquivo
// deixaria de rodar sob `node`.
export const STATUS_EM_ATENDIMENTO = 36;
export const OPERADOR_ANALISE_SUPORTE = 106;

/**
 * O que fazer com este ticket quando alguém clica em "Atender"?
 *
 * `pendencia` é o item da fila de pendentes do MySuite — o único lugar que traz
 * o dono (`codigoOperador`) e o status (`codigoStatusTicket`). `null` quando o
 * ticket não está na fila (já concluído, por exemplo).
 *
 * Devolve `{ pode, motivo }`, onde `pode` significa "faça o POST agora":
 *
 *   ok           · assume — é o único caso em que o POST sai;
 *   ja-e-meu     · já está em atendimento COM ESTE operador. Não há o que
 *                  marcar, e mesmo assim ele pode formalizar: é o clique
 *                  repetido no mesmo card, enquanto a fila não recarregou;
 *   ja-de-outro  · em atendimento com outra pessoa;
 *   de-outro     · não está em atendimento, mas o dono é outro operador;
 *   sem-operador · quem clicou não tem código em `orion.operador`; não há em
 *                  nome de quem marcar;
 *   desconhecido · o ticket não está na fila de pendentes. Sem saber o estado
 *                  dele, não se palpita — mesmo critério de
 *                  `verificarDonoDoTicket`.
 *
 * Um ticket EM ATENDIMENTO na fila genérica (106) ainda é assumido: "em
 * atendimento com a fila" não é atendimento de ninguém, e é justamente o que o
 * clique vem resolver.
 */
/**
 * O dono do ticket, venha de onde vier.
 *
 * Cada endpoint do MySuite dá um nome ao mesmo dado: a fila de pendentes chama
 * de `codigoOperador`, a consulta por status (`/tickets/filtro`) chama de
 * `operador`. A própria doc da API avisa: trate os dois com fallback.
 */
export function donoDoTicket(t) {
  const dono = Number(t?.codigoOperador ?? t?.operador);
  return Number.isFinite(dono) ? dono : null;
}

/**
 * O ticket está em "Sup - Em Atendimento"?
 *
 * Pelo código quando ele existe (`codigoStatusTicket` na fila de pendentes,
 * `codigoStatus` em alguns retornos) e pelo TEXTO quando não existe — que é o
 * caso de `/tickets/{n}` e de `/tickets/filtro`, os dois únicos lugares onde um
 * ticket no Setor de Sistemas aparece.
 */
export function estaEmAtendimento(t) {
  const cod = Number(t?.codigoStatusTicket ?? t?.codigoStatus);
  if (Number.isFinite(cod) && cod !== 0) return cod === STATUS_EM_ATENDIMENTO;
  return /em\s+atendimento/i.test(String(t?.status || ''));
}

export function decidirAssumir(pendencia, meuCodigo) {
  const meu = Number(meuCodigo);
  if (!Number.isFinite(meu) || !meu) return { pode: false, motivo: 'sem-operador' };

  if (!pendencia) return { pode: false, motivo: 'desconhecido' };

  const dono = donoDoTicket(pendencia);
  // Sem dono legível (campo ausente ou vazio) o ticket está solto na fila; a
  // fila de Análise Suporte é o mesmo caso, só que explícito.
  const semDono = dono === null || dono === 0 || dono === OPERADOR_ANALISE_SUPORTE;

  if (estaEmAtendimento(pendencia)) {
    if (dono === meu) return { pode: false, motivo: 'ja-e-meu' };
    if (semDono) return { pode: true, motivo: 'ok' };
    return { pode: false, motivo: 'ja-de-outro' };
  }

  if (!semDono && dono !== meu) return { pode: false, motivo: 'de-outro' };

  return { pode: true, motivo: 'ok' };
}

/** O ticket está em atendimento com quem clicou? (Assumir agora conta.) */
export function liberaFormalizacao(motivo) {
  return motivo === 'ok' || motivo === 'ja-e-meu';
}
